/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from "@auth0/auth0-react";
import React, { Suspense, lazy, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { FallbackView } from "../../_metronic/partials";
import { DashboardWrapper } from "../pages/dashboard/DashboardWrapper";
import { MenuTestPage } from "../pages/MenuTestPage";
import { CallbackPage } from "../pages/CallbackPage";
import { getUserCustomerAccess, PERMISSIONS } from "../../_metronic/helpers/UserRoleHelpers";
import { ProtectedRoute } from "../../_metronic/helpers/ProtectedRoute";

export function PrivateRoutes() {
  const BuilderPageWrapper = lazy(
    () => import("../pages/layout-builder/BuilderPageWrapper")
  );
  const ProfilePage = lazy(() => import("../modules/profile/ProfilePage"));
  const WizardsPage = lazy(() => import("../modules/wizards/WizardsPage"));
  const AccountPage = lazy(() => import("../modules/accounts/AccountPage"));
  const WidgetsPage = lazy(() => import("../modules/widgets/WidgetsPage"));
  const ReviewsPage = lazy(() => import("../pages/reviews/ReviewsPage"));
  const NpsPage = lazy(() => import("../pages/nps/NpsPage"));
  const CustomersPage = lazy(() => import("../pages/customers/CustomersPage"));
  const CacheManagerPage = lazy(() => import("../pages/admin/CacheManagerPage"));
  const AutoInjectionPage = lazy(() => import("../pages/admin/AutoInjectionPage"));
  const AiSummaryTestPage = lazy(() => import("../pages/admin/AiSummaryTestPage"));
  const MigrateReviewsPage = lazy(() => import("../pages/admin/MigrateReviewsPage"));
  const SlackOpsTrackerPage = lazy(() => import("../pages/admin/SlackOpsTrackerPage"));
  const AggregatesDiagnosticPage = lazy(() => import("../pages/admin/AggregatesDiagnosticPage"));
  const KioskDevicesPage = lazy(() => import("../pages/admin/KioskDevicesPage"));
  const PlatformUsagePage = lazy(() => import("../pages/admin/PlatformUsagePage"));
  const CsAlertsPage = lazy(() => import("../pages/admin/CsAlertsPage"));
  const CsAlertsHistoryPage = lazy(() => import("../pages/admin/CsAlertsHistoryPage"));
  const SyndicationAdmin = lazy(() => import("../pages/admin/SyndicationAdmin"));
  const SyndicationStore = lazy(() => import("../pages/syndication/SyndicationStore"));
  const StoreReviewsPage = lazy(
    () => import("../pages/store-reviews/StoreReviewsPage")
  );
  const QuestionsPage = lazy(() => import("../pages/questions/QuestionsPage"));
  const ProductsPage = lazy(() => import("../pages/products/ProductsPage"));
  const CategoriesPage = lazy(() => import("../pages/products/CategoriesPage"));
  const SellerReviewsPage = lazy(() => import("../pages/reviews/SellerReviewsPage"));
  const ReportsPage = lazy(() => import("../pages/reports/ReportsPage"));
  const ReviewsSettingsPage = lazy(
    () => import("../pages/reviews/SettingsPage")
  );
  const UsersPage = lazy(() => import("../pages/users/UsersPage"));
  const OrganizationPage = lazy(() => import("../pages/organization/OrganizationPage"));
  const AccessGroupsPage = lazy(() => import("../pages/admin/access-groups/AccessGroupsPage"));
  const ChatPage = lazy(() => import("../modules/apps/chat/ChatPage"));
  const NotificationsPage = lazy(
    () => import("../pages/notifications/NotificationsPage")
  );
  const OptOutManagementPage = lazy(
    () => import("../pages/reviews/OptOutManagementPage")
  );
  const BillingPage = lazy(() => import("../pages/billing/BillingPage"));
  const PlatformMigrationRoute = lazy(
    () => import("../pages/platform-migration/PlatformMigrationRoute")
  );
  const PlatformMigrationShopifyCallbackPage = lazy(
    () => import("../pages/platform-migration/ShopifyCallbackPage")
  );
  const PlatformMigrationOAuthCallbackPage = lazy(
    () => import("../pages/platform-migration/OAuthCallbackPage")
  );
  const ShopifyAppEntryPage = lazy(
    () => import("../pages/platform-migration/ShopifyAppEntryPage")
  );
  const OnboardingWizardPage = lazy(() => import("../pages/onboarding/OnboardingWizardPage"));
  const OnboardingCallbackPage = lazy(() => import("../pages/onboarding/OnboardingCallbackPage"));
  const RetrospectivePage = lazy(() => import("../pages/retrospective/RetrospectivePage"));
  const OverviewPage = lazy(() => import("../pages/overview/OverviewPage"));

  const { /*isAuthenticated,*/ user } = useAuth0();

  function useQuery() {
    const { search } = useLocation();
  
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const query = useQuery();

  const returnUrl = query.get("returnUrl");
  const redirectUrl = returnUrl ? decodeURIComponent(returnUrl) : "/dashboard";

  useEffect(() => {
    if (user) {
      const access = getUserCustomerAccess(user);
      try {
        // Set primary customer for analytics tracking
        const primaryCustomer = access.primaryCustomer || access.customers[0];
        (window as any).gtag("set", { customer: primaryCustomer });
      } catch (err) {}
    }
  }, []);

  return (
    <Suspense fallback={<FallbackView />}>
      <Switch>
        <Route path="/dashboard" component={DashboardWrapper} />
        <Route path="/overview" component={OverviewPage} />
        <Route path="/retrospective" component={RetrospectivePage} />
        <Route path="/builder" component={BuilderPageWrapper} />
        <Route path="/review-settings" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}>
            <ReviewsSettingsPage />
          </ProtectedRoute>
        )} />
        <Route path="/reviews" component={ReviewsPage} />
        <Route path="/nps/settings" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_NPS}>
            <NpsPage />
          </ProtectedRoute>
        )} />
        <Route path="/nps" component={NpsPage} />
        <Route path="/customers" component={CustomersPage} />
        <Route path="/cache-manager" component={CacheManagerPage} />
        <Route path="/auto-injection" component={AutoInjectionPage} />
        <Route path="/migrate-reviews" component={MigrateReviewsPage} />
        <Route path="/slack-ops-tracker" component={SlackOpsTrackerPage} />
        <Route path="/platform-usage" component={PlatformUsagePage} />
        <Route path="/aggregates-diagnostic" component={AggregatesDiagnosticPage} />
        {/* `ssoConfig` é escrita nossa: a tela é staff-only também no front, embora a
            API já exija requireKonfidencyAdmin. */}
        <Route path="/access-groups" render={() => (
          <ProtectedRoute isKonfidencyAdminOnly>
            <AccessGroupsPage />
          </ProtectedRoute>
        )} />
        <Route path="/kiosk-devices" component={KioskDevicesPage} />
        <Route path="/cs-alerts-history" component={CsAlertsHistoryPage} />
        <Route path="/cs-alerts" component={CsAlertsPage} />
        <Route path="/syndication/admin" component={SyndicationAdmin} />
        <Route path="/syndication" component={SyndicationStore} />
        <Route path="/ai-summary-test" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}>
            <AiSummaryTestPage />
          </ProtectedRoute>
        )} />
        <Route path="/store-reviews" component={StoreReviewsPage} />
        <Route path="/questions" component={QuestionsPage} />
        <Route path="/products/groups" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}>
            <ProductsPage />
          </ProtectedRoute>
        )} />
        <Route path="/products/kits" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}>
            <ProductsPage />
          </ProtectedRoute>
        )} />
        <Route path="/products/sellers/reviews" component={SellerReviewsPage} />
        <Route path="/products/categories" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}>
            <CategoriesPage />
          </ProtectedRoute>
        )} />
        <Route path="/products" component={ProductsPage} />
        <Route path="/reports" component={ReportsPage} />
        <Route path="/users" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_USERS}>
            <UsersPage />
          </ProtectedRoute>
        )} />
        {/* Grupo de acesso: mesma permissão da gestão de usuários — conceder acesso a
            uma loja é a mesma classe de ação que gerir quem já tem. */}
        <Route path="/organization" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_USERS}>
            <OrganizationPage />
          </ProtectedRoute>
        )} />
        <Route path="/notifications/opt-out" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_NOTIFICATIONS}>
            <OptOutManagementPage />
          </ProtectedRoute>
        )} />
        <Route path="/notifications" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MODERATE_REVIEWS}>
            <NotificationsPage />
          </ProtectedRoute>
        )} />
        <Route path="/billing" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}>
            <BillingPage />
          </ProtectedRoute>
        )} />
        {/* App URL fixa do app Shopify da loja: cada destino confere a própria permissão */}
        <Route path="/shopify/app/:customer" component={ShopifyAppEntryPage} />
        {/* Onboarding: o gate do MasterLayout leva para cá; cliente e state dos retornos vêm no caminho */}
        <Route path="/onboarding/callback/:platform(tray|nuvemshop)/:customer/:state" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_INTEGRATIONS}>
            <OnboardingCallbackPage />
          </ProtectedRoute>
        )} />
        <Route path="/onboarding/callback/:platform(shopify)/:customer" render={() => (
          <ProtectedRoute permission={PERMISSIONS.MANAGE_INTEGRATIONS}>
            <OnboardingCallbackPage />
          </ProtectedRoute>
        )} />
        <Route path="/onboarding" component={OnboardingWizardPage} />
        <Route path="/platform-migration/oauth-callback/:platform(tray|nuvemshop)/:customer/:state" render={() => (
          <ProtectedRoute permission={PERMISSIONS.PLATFORM_MIGRATE}>
            <PlatformMigrationOAuthCallbackPage />
          </ProtectedRoute>
        )} />
        <Route path="/platform-migration/shopify-callback/:customer" render={() => (
          <ProtectedRoute permission={PERMISSIONS.PLATFORM_MIGRATE}>
            <PlatformMigrationShopifyCallbackPage />
          </ProtectedRoute>
        )} />
        <Route path="/platform-migration" render={() => (
          <ProtectedRoute permission={PERMISSIONS.PLATFORM_MIGRATE}>
            <PlatformMigrationRoute />
          </ProtectedRoute>
        )} />
        <Route path="/crafted/pages/profile" component={ProfilePage} />
        <Route path="/crafted/pages/wizards" component={WizardsPage} />
        <Route path="/crafted/widgets" component={WidgetsPage} />
        <Route path="/crafted/account" component={AccountPage} />
        <Route path="/apps/chat" component={ChatPage} />
        <Route path="/menu-test" component={MenuTestPage} />
        <Route path="/callback/bling" component={lazy(() => import('../pages/integrations/BlingCallbackPage').then(module => ({ default: module.BlingCallbackPage })))} />
        <Route path="/callback/:platform" component={CallbackPage} />
        <Redirect from="/auth" to="/dashboard" />
        <Redirect exact from="/" to={redirectUrl} />
        <Redirect to="error/404" />
      </Switch>
    </Suspense>
  );
}
